import React, { useMemo, useState } from 'react';

import PackItemCard from './PackItemCard';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

const UNCATEGORIZED_KEY = '__uncategorized__';

export default function PackItemsByCategory({ items, categories, type, onEdit }) {
    const [collapsedIds, setCollapsedIds] = useState(() => new Set());

    const visibleItems = useMemo(() => (items || []).filter((item) => !item.deleted), [items]);

    const groups = useMemo(() => {
        const byCategory = new Map();
        byCategory.set(UNCATEGORIZED_KEY, { name: $t('Uncategorized'), items: [] });

        (categories || []).forEach((cat) => {
            byCategory.set(String(cat.id), { name: cat.name, categoryId: cat.id, items: [] });
        });

        visibleItems.forEach((item) => {
            const key = item.expense_pack_category_id != null ? String(item.expense_pack_category_id) : UNCATEGORIZED_KEY;
            const group = byCategory.get(key);
            if (group) {
                group.items.push(item);
            } else {
                byCategory.get(UNCATEGORIZED_KEY).items.push(item);
            }
        });

        const sorted = [];
        const catEntries = (categories || [])
            .slice()
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .map((cat) => [String(cat.id), byCategory.get(String(cat.id))])
            .filter(([, g]) => g && g.items.length > 0);
        catEntries.forEach(([, g]) => sorted.push(g));
        const uncat = byCategory.get(UNCATEGORIZED_KEY);
        if (uncat && uncat.items.length > 0) {
            sorted.push(uncat);
        }
        return sorted;
    }, [visibleItems, categories]);

    const toggle = (key) => {
        setCollapsedIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const isExpanded = (key) => !collapsedIds.has(key);

    if (!visibleItems.length) {
        return <p className="tw-text-sm tw-text-charcoal-50">{$t('No packs items found')}</p>;
    }

    return (
        <div className="tw-space-y-2">
            {groups.map((group) => {
                const key = group.categoryId != null ? String(group.categoryId) : UNCATEGORIZED_KEY;
                const expanded = isExpanded(key);
                return (
                    <div key={key} className="tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-overflow-hidden">
                        <button
                            type="button"
                            className="tw-w-full tw-p-2.5 tw-flex tw-items-center tw-gap-2 tw-text-left tw-text-sm tw-font-semibold tw-text-white tw-bg-gray-500 tw-border-b tw-border-gray-600 hover:tw-bg-gray-600"
                            onClick={() => toggle(key)}
                            aria-expanded={expanded}
                        >
                            <span className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-shrink-0 [&>svg]:tw-text-white">
                                {expanded ? (
                                    <ChevronUpIcon className="tw-w-3 tw-h-3 tw-text-white" aria-hidden />
                                ) : (
                                    <ChevronDownIcon className="tw-w-3 tw-h-3 tw-text-white" aria-hidden />
                                )}
                            </span>
                            <span>{group.name}</span>
                            <span className="tw-text-gray-200 tw-font-normal">({group.items.length})</span>
                        </button>
                        {expanded && (
                            <div className="tw-border-t tw-border-gray-300 tw-bg-gray-100 tw-overflow-hidden">
                                {group.items.map((packItem, index) => (
                                    <div
                                        key={packItem.id}
                                        className={
                                            'tw-bg-white tw-border-gray-200 ' +
                                            (index === 0 ? '' : 'tw-border-t ') +
                                            (index === group.items.length - 1 ? 'tw-rounded-b-lg' : '')
                                        }
                                    >
                                        <PackItemCard type={type} item={packItem} onEdit={() => onEdit(packItem.id)} fullSize={true} grouped />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
